import {
  Component,
  Input,
  EventEmitter,
  Output,
  HostListener,
  ElementRef,
  ViewChild,
  OnInit,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';

import { Tab } from 'app/shared/model/template-tab.model';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { ToastrService } from 'app/shared/service/toastr.service';

@Component({
  selector: 'cc-templates-list-tab-filter-item',
  templateUrl: './templates-list-tab-filter-item.component.html',
  styleUrls: [
    './templates-list-tab-filter-item.component.scss',
    '../../../../styles/components/template-tag-color.scss',
  ],
})
export class TemplatesListTabFilterItemComponent implements OnInit, AfterViewInit {
  @Input() tab: Tab;
  @Input() tabsNames: Record<string, boolean>;
  @Input() canEdit: boolean;
  @Input() sortValue: number;
  @Input() selected: boolean;
  @Output() addTab: EventEmitter<any> = new EventEmitter();
  @Output() editTab: EventEmitter<any> = new EventEmitter();
  @Output() deleteTab: EventEmitter<any> = new EventEmitter();
  @Output() selectTab: EventEmitter<any> = new EventEmitter();
  @Output() enableAddButton: EventEmitter<any> = new EventEmitter();
  @Output() removeNewTab: EventEmitter<any> = new EventEmitter();
  @Output() toggleDrag: EventEmitter<any> = new EventEmitter();
  @ViewChild('colorPicker') colorPicker: ElementRef;
  @ViewChild('tabNameInput') tabNameInput: ElementRef;
  @ViewChild('colorPickerContainer') colorPickerContainer: ElementRef;
  @ViewChild('scrollIntoViewFlag') scrollIntoViewFlag: ElementRef;

  colorPickerLeftPosition: number;
  styles = [
    'light-blue',
    'dark-blue',
    'red',
    'orange',
    'yellow',
    'lime',
    'green',
    'sky',
    'purple',
    'pink',
    'grey',
  ];

  prevValues: { name: string; style: string };
  showPicker: boolean;
  editing = false;
  updating = false;
  isNew = false;
  creating = false;
  id: string;
  name: string;
  style: string;

  constructor(
    private eRef: ElementRef,
    private confirmService: ConfirmService,
    private readonly toastrService: ToastrService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.id = this.tab.id;
    this.style = this.tab.style;
    this.name = this.tab.name;
    this.isNew = this.tab.new;
  }

  ngAfterViewInit() {
    if (this.isNew) {
      this.creating = true;
      this.focusInput();
      this.scrollIntoView();
    }

    if (this.selected) {
      // Timeout set wait for other tabs to loaded before scrolling into view
      setTimeout(() => {
        this.scrollIntoView();
      }, 100);
    }
  }

  select() {
    if (!this.selected) {
      this.selectTab.emit(this.id);
    }
  }

  addNewTab() {
    this.name = this.name.trim();

    if (this.validAdd()) {
      this.updating = true;
      this.creating = false;
      this.isNew = false;
      this.addTab.emit({
        id: this.id,
        name: this.name,
        style: this.style,
        sortValue: this.sortValue,
      });
      this.enableAddButton.emit();
    }
  }

  cancelAdd() {
    this.removeNewTab.emit();
    this.enableAddButton.emit();
  }

  enableEdit() {
    this.editing = true;
    this.toggleDrag.emit(this.id);
    this.prevValues = {
      name: this.name,
      style: this.style,
    };
    this.changeDetectorRef.detectChanges();
    this.scrollIntoView();
    this.focusInput();
  }

  cancelEdit() {
    this.name = this.prevValues.name;
    this.style = this.prevValues.style;
    this.editing = false;
    this.showPicker = false;
    this.toggleDrag.emit(this.id);
  }

  updateTab() {
    this.name = this.name.trim();

    if (this.validUpdate()) {
      this.editing = false;
      this.updating = true;
      this.editTab.emit({
        id: this.id,
        name: this.name,
        style: this.style,
        sortValue: this.sortValue,
      });
    }
  }

  delete() {
    void this.confirmService
      .confirmDelete('deleteTemplateTab')
      .then(() => {
        this.updating = true;
        this.editing = false;
        this.deleteTab.emit(this.id);
        this.toggleDrag.emit(this.id);
        return true;
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {});
  }

  togglePicker() {
    this.colorPickerLeftPosition = this.colorPicker.nativeElement.getBoundingClientRect().left;
    this.showPicker = !this.showPicker;
  }

  selectStyle(style) {
    this.style = style;
    this.showPicker = false;
  }

  @HostListener('document:click', ['$event'])
  clickOut(event) {
    if (!this.eRef.nativeElement.contains(event.target) && this.editing && !this.updating) {
      this.updateTab();
    }

    if (!this.eRef.nativeElement.contains(event.target) && this.isNew && !this.updating) {
      // Flag to avoid click event triggering the first time when pushing a new tab
      if (!this.creating) {
        this.addNewTab();
      }
      this.creating = false;
    }
  }

  private validUpdate() {
    if (!this.name || !this.tabValuesChanged()) {
      this.cancelEdit();
      return false;
    }

    if (this.tabStyleChanged() && !this.tabNameChanged()) {
      return true;
    }

    if (!this.isNameUnique()) {
      this.toastrService.errorByKey('uniqueNameTemplateTab');
      this.focusInput();
      return false;
    }

    return true;
  }

  private validAdd() {
    if (!this.name) {
      this.cancelAdd();
      return false;
    }

    if (!this.isNameUnique()) {
      this.toastrService.errorByKey('uniqueNameTemplateTab');
      this.focusInput();
      return false;
    }

    return true;
  }

  private isNameUnique() {
    return !this.tabsNames[this.name];
  }

  private focusInput() {
    this.tabNameInput.nativeElement.focus({ preventScroll: true });
  }

  private scrollIntoView() {
    this.scrollIntoViewFlag.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  private tabValuesChanged() {
    return this.tabNameChanged() || this.tabStyleChanged();
  }

  private tabNameChanged(): boolean {
    return this.name !== this.prevValues.name;
  }

  private tabStyleChanged(): boolean {
    return this.style !== this.prevValues.style;
  }
}
